/**
 * FRONTEND DELETE HELPER
 * ============================================================
 * Centralizes delete error handling across all pages.
 *
 * Usage:
 *   import { confirmDelete, handleDeleteError } from '../utils/deleteHelper';
 *
 *   const ok = confirmDelete('employee', 'John Smith');
 *   if (!ok) return;
 *   try {
 *     await api.delete(`/api/employees/${id}`);
 *     load(); // refresh list
 *   } catch (err) {
 *     handleDeleteError(err);
 *   }
 * ============================================================
 */

/**
 * Show a confirmation dialog before deleting.
 * Returns true if user confirmed.
 */
export function confirmDelete(entityLabel, recordName = '') {
  const name = recordName ? ` "${recordName}"` : '';
  return window.confirm(
    `Are you sure you want to deactivate${name}?\n\nThis ${entityLabel} will be hidden from all active screens. Historical records will remain intact.`
  );
}

/**
 * Handle errors from a delete API call.
 * Shows a friendly popup for blocked deletes (400) and permission errors (403).
 */
export function handleDeleteError(err) {
  const status = err?.response?.status;
  const data   = err?.response?.data;

  if (status === 403) {
    showDeleteBlocked(
      'Permission Denied',
      data?.message || 'Only System Administrators can delete records.'
    );
    return;
  }

  if (status === 400) {
    showDeleteBlocked(
      'Delete Failed',
      data?.message || 'This record is linked with system data and cannot be removed.'
    );
    return;
  }

  // Generic error
  alert(`Delete failed: ${data?.error || data?.message || err.message || 'Unknown error'}`);
}

/**
 * Show a styled blocking dialog (uses native alert with clear formatting).
 */
function showDeleteBlocked(title, message) {
  alert(`⛔ ${title}\n\n${message}`);
}
